import { useEffect, useMemo, useState } from "react";
import AppShell from "@/components/AppShell";
import { PageHeader } from "@/components/PageHeader";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Download,
  Package,
  FileSpreadsheet,
  Loader2,
  CheckCircle2,
  Clock,
  Calendar,
  ChevronDown,
  ChevronRight,
} from "lucide-react";

export default function AdminConsolidadoPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloadingAll, setDownloadingAll] = useState(false);
  const [downloadingId, setDownloadingId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/consolidado");
      setRows(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const totals = useMemo(() => {
    return rows.reduce(
      (acc, r) => {
        acc.rows += r.total_rows;
        acc.delivered += r.delivered;
        acc.pending += r.pending;
        return acc;
      },
      { rows: 0, delivered: 0, pending: 0, clients: rows.length }
    );
  }, [rows]);

  const downloadOne = async (row, month = null) => {
    setDownloadingId(`${row.user_id}${month ? `_${month}` : ""}`);
    try {
      const url = month
        ? `/admin/consolidado/${row.user_id}/download?month=${month}`
        : `/admin/consolidado/${row.user_id}/download`;
      const res = await api.get(url, { responseType: "blob" });
      const bloburl = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = bloburl;
      const clientTag = (
        row.id_cliente || row.razon_social || "cliente"
      ).replace(/\s+/g, "_");
      const monthTag = month ? `_${month.replace("-", "")}` : "";
      a.download = `Consolidado_SAP_RDA_${clientTag}${monthTag}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(bloburl);
      toast.success(`Consolidado descargado: ${row.razon_social}`);
    } catch (e) {
      const msg =
        e.response?.data instanceof Blob
          ? await e.response.data.text().then((t) => {
              try {
                return JSON.parse(t).detail;
              } catch {
                return t;
              }
            })
          : formatApiError(e.response?.data?.detail);
      toast.error(msg || "Error al descargar");
    } finally {
      setDownloadingId(null);
    }
  };

  const downloadAll = async () => {
    if (rows.length === 0) {
      toast.error("No hay clientes con datos consolidables");
      return;
    }
    setDownloadingAll(true);
    try {
      const res = await api.get("/admin/consolidado/download-all", {
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      const ts = new Date()
        .toISOString()
        .slice(0, 16)
        .replace(/[-:T]/g, "");
      a.download = `Consolidado_SAP_RDA_EXIS_PHARMA_${ts}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("ZIP consolidado descargado");
    } catch (e) {
      const msg =
        e.response?.data instanceof Blob
          ? await e.response.data.text().then((t) => {
              try {
                return JSON.parse(t).detail;
              } catch {
                return t;
              }
            })
          : formatApiError(e.response?.data?.detail);
      toast.error(msg || "Error al descargar ZIP");
    } finally {
      setDownloadingAll(false);
    }
  };

  return (
    <AppShell>
      <div className="max-w-7xl mx-auto p-6 md:p-10">
        <PageHeader
          eyebrow="Administración · Cierre operativo"
          title="Consolidado SAP + RDA"
          description="Un archivo Excel por cliente que combina los productos despachados (SAP) con la confirmación de entrega (RDA). Descarga individual o masiva en ZIP."
          actions={
            <button
              onClick={downloadAll}
              disabled={downloadingAll || rows.length === 0}
              data-testid="consolidado-download-all-btn"
              className="flex items-center gap-2 px-5 py-3 bg-[#D95A40] hover:bg-[#c94d33] disabled:opacity-50 disabled:cursor-not-allowed text-white font-bold uppercase tracking-[0.15em] text-xs transition-colors"
            >
              {downloadingAll ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" /> Generando ZIP...
                </>
              ) : (
                <>
                  <Package className="w-4 h-4" /> Descargar todos (ZIP)
                </>
              )}
            </button>
          }
        />

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Kpi
            label="Clientes"
            value={totals.clients}
            icon={<FileSpreadsheet className="w-4 h-4" />}
          />
          <Kpi
            label="Filas totales"
            value={totals.rows}
            icon={<FileSpreadsheet className="w-4 h-4" />}
          />
          <Kpi
            label="Entregadas"
            value={totals.delivered}
            icon={<CheckCircle2 className="w-4 h-4" />}
            good
          />
          <Kpi
            label="Pendientes"
            value={totals.pending}
            icon={<Clock className="w-4 h-4" />}
            warn={totals.pending > 0}
          />
        </div>

        <div className="bg-white border border-slate-200 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#0B1B3D] text-white">
              <tr>
                <Th>Empresa</Th>
                <Th>ID Cliente</Th>
                <Th className="text-right">Filas SAP</Th>
                <Th className="text-right">Entregadas</Th>
                <Th className="text-right">Pendientes</Th>
                <Th className="text-right">Cumplimiento</Th>
                <Th className="text-right">Acción</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading && (
                <tr>
                  <td colSpan={7} className="p-10 text-center text-slate-500">
                    Cargando...
                  </td>
                </tr>
              )}
              {!loading && rows.length === 0 && (
                <tr>
                  <td colSpan={7} className="p-10 text-center text-slate-500">
                    Sin clientes con datos SAP para consolidar.
                  </td>
                </tr>
              )}
              {rows.map((r) => {
                const pct = r.total_rows
                  ? Math.round((r.delivered / r.total_rows) * 100)
                  : 0;
                return (
                  <ClientRow
                    key={r.user_id}
                    r={r}
                    pct={pct}
                    downloadingId={downloadingId}
                    onDownload={downloadOne}
                  />
                );
              })}
            </tbody>
          </table>
        </div>

        <p className="text-xs text-slate-500 mt-4">
          El consolidado combina las <strong>15 columnas SAP</strong> con las
          columnas del RDA. Los archivos se generan{" "}
          <span className="text-[#D95A40] font-bold">por mes</span> — cada mes
          con envíos genera un archivo independiente. Descarga masiva en ZIP
          incluye un archivo por cliente por mes.
        </p>
      </div>
    </AppShell>
  );
}

function ClientRow({ r, pct, downloadingId, onDownload }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <tr
        data-testid={`consolidado-row-${r.user_id}`}
        className="hover:bg-slate-50"
      >
        <td className="px-4 py-3">
          <button
            onClick={() => setOpen(!open)}
            className="flex items-center gap-2 text-left"
          >
            {open ? (
              <ChevronDown className="w-3.5 h-3.5 text-slate-500" />
            ) : (
              <ChevronRight className="w-3.5 h-3.5 text-slate-500" />
            )}
            <div>
              <div className="font-semibold text-[#0B1B3D]">
                {r.razon_social || r.name}
              </div>
              <div className="text-xs text-slate-500 font-mono">{r.email}</div>
            </div>
          </button>
        </td>
        <td className="px-4 py-3 text-slate-700">{r.id_cliente || "—"}</td>
        <td className="px-4 py-3 text-right font-display font-bold text-[#0B1B3D] tabular-nums">
          {r.total_rows}
        </td>
        <td className="px-4 py-3 text-right tabular-nums">
          <span className="inline-flex items-center gap-1 text-emerald-700 font-semibold">
            <CheckCircle2 className="w-3 h-3" />
            {r.delivered}
          </span>
        </td>
        <td className="px-4 py-3 text-right tabular-nums">
          {r.pending > 0 ? (
            <span className="inline-block bg-[#D95A40]/10 text-[#D95A40] px-2 py-0.5 font-bold">
              {r.pending}
            </span>
          ) : (
            <span className="text-slate-400">0</span>
          )}
        </td>
        <td className="px-4 py-3">
          <div className="flex items-center gap-3 justify-end">
            <div className="h-1.5 bg-slate-100 w-28 relative">
              <div
                className={`h-full ${
                  pct === 100
                    ? "bg-emerald-500"
                    : pct > 0
                    ? "bg-[#D95A40]"
                    : "bg-slate-300"
                }`}
                style={{ width: `${pct}%` }}
              />
            </div>
            <span className="text-xs font-bold tabular-nums w-10 text-right">
              {pct}%
            </span>
          </div>
        </td>
        <td className="px-4 py-3 text-right">
          <button
            onClick={() => setOpen(!open)}
            data-testid={`consolidado-toggle-${r.user_id}`}
            className="inline-flex items-center gap-2 px-3 py-1.5 border border-slate-300 hover:border-[#0B1B3D] text-slate-700 hover:text-[#0B1B3D] text-xs font-bold uppercase tracking-[0.1em] transition-colors"
          >
            {open ? "Cerrar" : `Ver ${r.months?.length || 0} meses`}
          </button>
        </td>
      </tr>
      {open && (
        <tr>
          <td colSpan={7} className="p-0 bg-slate-50">
            <div className="px-6 py-4 border-t border-slate-200">
              <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold mb-3">
                Archivos por mes
              </div>
              <div className="grid gap-2">
                {(r.months || []).map((m) => (
                  <div
                    key={m.month}
                    data-testid={`consolidado-month-${r.user_id}-${m.month}`}
                    className="bg-white border border-slate-200 p-3 flex items-center justify-between gap-3"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <Calendar className="w-4 h-4 text-slate-400 shrink-0" />
                      <div className="min-w-0">
                        <div className="font-display font-bold text-[#0B1B3D]">
                          {m.label}
                        </div>
                        <div className="text-xs text-slate-500 mt-0.5">
                          {m.batches} envíos · {m.docs} docs · {m.total_rows}{" "}
                          filas ·{" "}
                          <span className="text-emerald-700 font-semibold">
                            {m.delivered} entregadas
                          </span>{" "}
                          ·{" "}
                          <span className="text-[#D95A40]">
                            {m.pending} pendientes
                          </span>
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={() => onDownload(r, m.month)}
                      disabled={
                        downloadingId === `${r.user_id}_${m.month}`
                      }
                      data-testid={`consolidado-download-${r.user_id}-${m.month}`}
                      className="flex items-center gap-2 px-3 py-1.5 bg-[#D95A40] hover:bg-[#c94d33] disabled:opacity-50 text-white text-xs font-bold uppercase tracking-wider shrink-0"
                    >
                      {downloadingId === `${r.user_id}_${m.month}` ? (
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      ) : (
                        <Download className="w-3.5 h-3.5" />
                      )}
                      Descargar
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

function Kpi({ label, value, icon, good, warn }) {
  const border = good
    ? "border-emerald-300 bg-emerald-50/40"
    : warn
    ? "border-[#D95A40] bg-[#D95A40]/5"
    : "border-slate-200 bg-white";
  const iconColor = good
    ? "text-emerald-600"
    : warn
    ? "text-[#D95A40]"
    : "text-[#D95A40]";
  return (
    <div className={`border p-4 ${border}`}>
      <div className="flex items-start justify-between mb-2">
        <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">
          {label}
        </div>
        <div className={iconColor}>{icon}</div>
      </div>
      <div className="font-display font-black text-3xl text-[#0B1B3D] tabular-nums leading-none">
        {value}
      </div>
    </div>
  );
}

function Th({ children, className = "" }) {
  return (
    <th
      className={`px-4 py-3 text-left text-[11px] font-bold uppercase tracking-[0.15em] ${className}`}
    >
      {children}
    </th>
  );
}
